import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { allGoals, BroadmatchGroups, BroadmatchValue, ChangedOptions } from '../../typings/log';
import { ExpandableCell, ExpandableProps } from '../ExpandableCell/ExpandableCell';

import './BroadmatchCell.css';
import * as keyset from './BroadmatchCell.i18n';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);

export interface BroadmatchProps extends ExpandableProps {
    diff: BroadmatchGroups;
}

export class BroadmatchCell extends ExpandableCell<BroadmatchProps> {
    protected getContent() {
        const {
                diff: { before, after }
            } = this.props,
            broadmatchWasOn = before.enabled,
            broadmatchIsNowOn = after.enabled,
            broadmatchWasEnabled = !broadmatchWasOn && broadmatchIsNowOn,
            broadmatchWasDisabled = !broadmatchIsNowOn && broadmatchWasOn,
            broadmatchWasChanged = broadmatchWasOn && broadmatchIsNowOn;

        let block: React.ReactNode;
        if (broadmatchWasEnabled) {
            block = this.getBroadmatchEnabled();
        } else if (broadmatchWasDisabled) {
            block = this.getBroadmatchDisabled();
        } else if (broadmatchWasChanged) {
            block = this.getBroadmatchDiff();
        } else {
            throw Error(
                'Неверное изменение: выключение уже отключенного ДРФ. Рекомендуем обратиться в службу поддержки'
            );
        }

        return (
            <div className={Bem.cls('broadmatch-cell')} ref={ref => (this.cell = ref)}>
                {block}
            </div>
        );
    }

    private getBroadmatchEnabled = (): React.ReactNode => {
        const {
            diff: { after }
        } = this.props;
        return this.getGroup(ChangedOptions.added, after);
    };

    private getBroadmatchDisabled = (): React.ReactNode => {
        const {
            diff: { before }
        } = this.props;
        return <div>{this.getGroup(ChangedOptions.removed, before)}</div>;
    };

    private getBroadmatchDiff = (): React.ReactNode => {
        return (
            <div>
                <div key={'diff'} className={Bem.cls('broadmatch-cell', 'row')}>
                    {this.getLimitDiff()}
                    {this.getGoalDiff()}
                </div>
            </div>
        );
    };

    private getLimitDiff = (): React.ReactNode => {
        const {
                diff: { before, after },
                isExpanded,
                canExpand
            } = this.props,
            beforeLimit = before.limit,
            afterLimit = after.limit,
            isEqual = beforeLimit === afterLimit;

        let span;

        if (isEqual) {
            span = i18n('campaign_broad_match_limit', { limit: afterLimit });
        } else {
            const oldValueSpan = (
                    <span key={'old_limit'} className={Bem.cls('broadmatch-cell', 'removed')}>
                        {beforeLimit}%
                    </span>
                ),
                newValueSpan = (
                    <span key={'new_limit'} className={Bem.cls('broadmatch-cell', 'added')}>
                        {afterLimit}%
                    </span>
                );
            span = i18nRaw('campaign_broad_match_limit_changed', {
                oldLimit: oldValueSpan,
                newLimit: newValueSpan
            });
        }

        return (
            <div
                key={'limit_diff'}
                className={classNames(
                    Bem.cls('broadmatch-cell', 'cell'),
                    !isExpanded && canExpand ? Bem.cls('expandable-cell', 'short-row') : ''
                )}
            >
                {span}
            </div>
        );
    };

    private getGoalDiff = (): React.ReactNode => {
        const {
                diff: {
                    before: {
                        goal: { id: beforeId, name: beforeName }
                    },
                    after: {
                        goal: { id: afterId, name: afterName }
                    }
                },
                isExpanded,
                canExpand
            } = this.props,
            {
                diff: { before, after }
            } = this.props,
            isEqual = beforeId === afterId;

        let span;

        if (isEqual) {
            span = this.getGoalDiv(after);
        } else if (beforeId === undefined) {
            span = (
                <div key={'added_goal'} className={Bem.cls('broadmatch-cell', 'added')}>
                    {this.getGoalDiv(after)}
                </div>
            );
        } else if (afterId === undefined) {
            span = (
                <div key={'removed_goal'} className={Bem.cls('broadmatch-cell', 'removed')}>
                    {this.getGoalDiv(before)}
                </div>
            );
        } else {
            const oldValueSpan = (
                    <span key={'old_goal'} className={Bem.cls('broadmatch-cell', 'removed')}>
                        {beforeId === allGoals ? i18n('on-all-goals') : i18n('on-goal', { goalName: beforeName })}
                    </span>
                ),
                newValueSpan = (
                    <span key={'new_goal'} className={Bem.cls('broadmatch-cell', 'added')}>
                        {afterId === allGoals ? i18n('on-all-goals') : i18n('on-goal', { goalName: afterName })}
                    </span>
                );
            span = i18nRaw('campaign_broad_match_goal_changed', {
                oldGoal: oldValueSpan,
                newGoal: newValueSpan
            });
        }

        return (
            <div
                key={'goal_diff'}
                className={classNames(
                    Bem.cls('broadmatch-cell', 'cell'),
                    !isExpanded && canExpand ? Bem.cls('expandable-cell', 'short-row') : ''
                )}
            >
                {span}
            </div>
        );
    };

    private getGroup = (type: ChangedOptions, broadmatchValue?: BroadmatchValue, text?: string): React.ReactNode => {
        const { isExpanded, canExpand } = this.props;

        return (
            <div
                className={classNames(
                    Bem.cls('broadmatch-cell', 'row'),
                    Bem.cls('broadmatch-cell', type.toLowerCase()),
                    !isExpanded && canExpand ? Bem.cls('expandable-cell', 'short-row') : ''
                )}
            >
                {broadmatchValue ? this.getBroadmatchText(broadmatchValue) : text}
            </div>
        );
    };

    private getGoalDiv = (broadmatchValue: BroadmatchValue): React.ReactNode => {
        const {
                goal: { id, name }
            } = broadmatchValue,
            hasGoal = id !== undefined,
            goalText =
                id === allGoals
                    ? i18n('campaign_broad_match_goal_all')
                    : i18n('campaign_broad_match_goal_id', {
                          goalName: name ? name : id
                      });
        return hasGoal ? (
            <div key={Math.random()} className={classNames(Bem.cls('broadmatch-cell', 'cell'))}>
                {goalText}
            </div>
        ) : (
            ''
        );
    };

    private getBroadmatchText = (broadmatchValue: BroadmatchValue): React.ReactNode => {
        return (
            <span>
                <span className={classNames(Bem.cls('broadmatch-cell', 'cell'))}>
                    {i18n('campaign_broad_match_limit', {
                        limit: broadmatchValue.limit
                    })}
                </span>
                {this.getGoalDiv(broadmatchValue)}
            </span>
        );
    };
}
